import { useEffect, useRef, useState } from "react";
import { Store, Upload, Download } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import SearchableSelect from "@/components/SearchableSelect";
import ListSearchBar, { LoadMore } from "@/components/ListSearchBar";
import usePagedSearch, { useDebounced } from "@/hooks/usePagedSearch";

const STATUS_OPTIONS = {
  vendors: [["", "Any status"], ["approved", "Approved"], ["pending", "Pending"], ["declined", "Declined"]],
  contractors: [["", "Any status"], ["active", "Active"], ["pending", "Pending"], ["declined", "Declined"]],
};

// Every vendor and contractor on the platform, searched and paged on the server.
export default function Directories() {
  const [tab, setTab] = useState("vendors");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [managing, setManaging] = useState(null); // vendor being catalog-managed
  const q = useDebounced(search);
  const vendors = usePagedSearch("/staff/vendors/search", { q, status: tab === "vendors" ? status : "" });
  const contractors = usePagedSearch("/staff/contractors/search", { q, status: tab === "contractors" ? status : "" });
  const total = (list) => list.counts?.[status || "all"] ?? 0;

  return (
    <div className="p-6 md:p-8" data-testid="directories-page">
      <h1 className="text-2xl font-extrabold tracking-tight">Directories</h1>
      <p className="text-slate-500 text-sm mt-1">All vendors and contractors on the platform.</p>

      <ListSearchBar value={search} onChange={setSearch} placeholder={tab === "vendors" ? "Search vendor name, category, email, phone…" : "Search contractor name, industry, address, phone…"} testId="directories-search">
        <SearchableSelect value={status} onChange={(e) => setStatus(e.target.value)} className="h-10 px-3 rounded-lg border border-slate-200 text-[13px] min-w-[150px]" data-testid="directories-status-filter">
          {STATUS_OPTIONS[tab].map(([v, label]) => <option key={v || "any"} value={v}>{label}</option>)}
        </SearchableSelect>
      </ListSearchBar>

      <Tabs value={tab} onValueChange={(v) => { setTab(v); setStatus(""); }} className="mt-5">
        <TabsList>
          <TabsTrigger value="vendors" data-testid="tab-all-vendors">Vendors ({total(vendors).toLocaleString()})</TabsTrigger>
          <TabsTrigger value="contractors" data-testid="tab-all-contractors">Contractors ({total(contractors).toLocaleString()})</TabsTrigger>
        </TabsList>
        <TabsContent value="vendors" className="mt-5">
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead><tr className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold border-b border-slate-100"><th className="text-left py-2.5">Name</th><th className="text-left py-2.5">Category</th><th className="text-left py-2.5">Contact</th><th className="text-left py-2.5">Status</th><th></th></tr></thead>
              <tbody>
                {(vendors.items || []).map((v) => (
                  <tr key={v.id} className="border-b border-slate-50" data-testid={`vendor-dir-row-${v.id}`}>
                    <td className="py-3 font-medium">{v.name}</td><td className="py-3 text-slate-500">{v.category}</td><td className="py-3 text-slate-500">{v.contact_email}</td><td className="py-3"><StatusBadge status={v.status} /></td>
                    <td className="py-3 text-right">
                      <button onClick={() => setManaging(v)} data-testid={`manage-catalog-${v.id}`} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-[12px] font-semibold hover:bg-slate-50 ml-auto">
                        <Store size={12} /> Manage Catalog
                      </button>
                    </td>
                  </tr>
                ))}
                {vendors.items && vendors.items.length === 0 && <tr><td colSpan={5} className="py-10 text-center text-slate-400 text-sm">No vendors match.</td></tr>}
              </tbody>
            </table>
          </div>
          <LoadMore items={vendors.items} nextCursor={vendors.nextCursor} loadMore={vendors.loadMore} loadingMore={vendors.loadingMore} total={total(vendors)} />
        </TabsContent>
        <TabsContent value="contractors" className="mt-5">
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead><tr className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold border-b border-slate-100"><th className="text-left py-2.5">Name</th><th className="text-left py-2.5">Industry</th><th className="text-left py-2.5">Tier</th><th className="text-left py-2.5">Status</th></tr></thead>
              <tbody>
                {(contractors.items || []).map((c) => (
                  <tr key={c.id} className="border-b border-slate-50" data-testid={`contractor-dir-row-${c.id}`}>
                    <td className="py-3 font-medium">{c.name}</td><td className="py-3 text-slate-500">{c.industry}</td><td className="py-3 text-slate-500 capitalize">{c.subscription_tier}</td><td className="py-3"><StatusBadge status={c.status} /></td>
                  </tr>
                ))}
                {contractors.items && contractors.items.length === 0 && <tr><td colSpan={4} className="py-10 text-center text-slate-400 text-sm">No contractors match.</td></tr>}
              </tbody>
            </table>
          </div>
          <LoadMore items={contractors.items} nextCursor={contractors.nextCursor} loadMore={contractors.loadMore} loadingMore={contractors.loadingMore} total={total(contractors)} />
        </TabsContent>
      </Tabs>

      <VendorCatalogManager vendor={managing} open={!!managing} onOpenChange={(v) => !v && setManaging(null)} />
    </div>
  );
}

// Staff-assisted catalog management (Package 19) — works while the vendor
// portal is parked: list the vendor's products, download the CSV template,
// and import a catalog CSV on the vendor's behalf.
function VendorCatalogManager({ vendor, open, onOpenChange }) {
  const [products, setProducts] = useState([]);
  const [importing, setImporting] = useState(false);
  const [errors, setErrors] = useState([]);
  const fileRef = useRef(null);

  const load = () => {
    if (!vendor) return;
    api.get(`/staff/vendors/${vendor.id}/catalog`).then((r) => setProducts(r.data.products));
  };
  useEffect(() => { if (open) { setErrors([]); load(); } }, [open, vendor?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const downloadTemplate = async () => {
    const res = await api.get(`/staff/vendors/${vendor.id}/catalog/import/template`, { responseType: "blob" });
    const url = window.URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = "catalog-template.csv"; a.click();
    window.URL.revokeObjectURL(url);
  };

  const importCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/staff/vendors/${vendor.id}/catalog/import`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setErrors(data.errors);
      toast[data.errors.length ? "warning" : "success"](
        `Imported ${data.imported} new, updated ${data.updated}${data.errors.length ? `, ${data.errors.length} row error(s)` : ""}`,
      );
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Import failed");
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  if (!vendor) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto" data-testid="staff-catalog-manager">
        <SheetHeader><SheetTitle>Catalog — {vendor.name}</SheetTitle></SheetHeader>
        <div className="flex gap-2 mt-4">
          <button onClick={downloadTemplate} data-testid="staff-csv-template-button" className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg border border-slate-200 text-[12.5px] font-semibold hover:bg-slate-50">
            <Download size={13} /> CSV Template
          </button>
          <button onClick={() => fileRef.current?.click()} disabled={importing} data-testid="staff-csv-import-button" className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-[12.5px] font-semibold disabled:opacity-60">
            <Upload size={13} /> {importing ? "Importing…" : "Import CSV / Excel for this vendor"}
          </button>
          <input ref={fileRef} type="file" accept=".csv,text/csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={importCsv} className="hidden" />
        </div>

        {errors.length > 0 && (
          <div className="mt-3 text-[12px] text-red-800 bg-red-50 border border-red-100 rounded-lg px-3.5 py-2.5" data-testid="staff-import-errors">
            {errors.slice(0, 8).map((er, i) => <div key={i}>Row {er.row}: {er.message}</div>)}
            {errors.length > 8 && <div>…and {errors.length - 8} more</div>}
          </div>
        )}

        <table className="w-full text-[12.5px] mt-4">
          <thead>
            <tr className="text-[10.5px] uppercase tracking-wide text-slate-400 font-semibold border-b border-slate-100">
              <th className="text-left py-2">Product</th><th className="text-left py-2">SKU</th><th className="text-right py-2">Price</th><th className="text-left py-2 pl-3">Stock</th>
            </tr>
          </thead>
          <tbody>
            {products.map((p) => (
              <tr key={p.id} className="border-b border-slate-50">
                <td className="py-2">{p.name}</td>
                <td className="py-2 mono text-slate-500">{p.sku || "—"}</td>
                <td className="py-2 text-right mono">${Number(p.price || 0).toFixed(2)}</td>
                <td className="py-2 pl-3"><StatusBadge status={p.stock_status} /></td>
              </tr>
            ))}
            {products.length === 0 && <tr><td colSpan={4} className="py-8 text-center text-slate-400">No products yet — import a CSV to get started.</td></tr>}
          </tbody>
        </table>
      </SheetContent>
    </Sheet>
  );
}
